import {
  Button,
  Editor,
  Input,
  RadioButtonGroup,
  SegmentedInput,
  Select,
  TextArea,
  Toggle,
} from "@mixitone/components";
import { centsToDollars, dollarsToCents } from "@mixitone/util";
import { ApplicationView } from "@mixitone/mvc";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import type React from "react";
import type {
  CheckboxField,
  CustomField,
  DatetimeField,
  FormFieldConfig,
  FormFieldOption,
  HtmlField,
  NumberField,
  DollarsField,
  SegmentedField,
  SelectField,
  TextField,
  TimeField,
  UploadField,
} from "./FormFieldsDefinition";
import { FormController } from "./FormController";
import { ImageUpload } from "./ImageUpload";
import { PdfUpload } from "./PdfUpload";

type FormFieldValue<F extends FormFieldConfig> = F extends { value: () => infer V }
  ? V
  : F extends TextField
    ? string
    : F extends NumberField
      ? number
      : F extends SelectField<any>
        ? string
        : F extends CheckboxField
          ? boolean
          : F extends DatetimeField
            ? string
            : F extends TimeField
              ? string
              : F extends CustomField
                ? any
                : never;

// FormFieldConfig with onChange and value made required but retaining type
type FormField<F extends FormFieldConfig> = F & {
  onChange: (value: FormFieldValue<F>) => void;
  value: () => FormFieldValue<F>;
  validate: (value: FormFieldValue<F>) => string | undefined;
};

/**
 * Props for value components after field binding
 */
export interface BoundValueComponentProps<F extends FormFieldConfig> {
  field: FormField<F>;
  record: any;
  autoFocus?: boolean;
  variant?: string;
  className?: string;
  error?: string;
}

export interface ValueComponentProps {
  field: FormFieldConfig;
  record: any;
  autoFocus?: boolean;
  variant?: string;
  className?: string;
  error?: string;
}

export function isRequired(field: FormFieldConfig, record: any): boolean {
  return typeof field.required === "function" ? field.required(record) : (field.required ?? false);
}
function isDisabled(field: FormFieldConfig, record: any): boolean {
  return typeof field.disabled === "function" ? field.disabled(record) : (field.disabled ?? false);
}

/**
 * Registry of value component factories for different field types
 */
export const ValueComponents = {
  text: ({ field, record, ...props }: BoundValueComponentProps<TextField>) => {
    if (field.inputType === "textarea") {
      return (
        <TextArea
          id={field.name}
          name={field.name}
          aria-label={field.label}
          required={isRequired(field, record)}
          placeholder={field.placeholder}
          rows={field.rows}
          minLength={field.minLength}
          maxLength={field.maxLength}
          onChange={(e) => field.onChange(e.target.value)}
          value={field.value()}
          disabled={isDisabled(field, record)}
          {...props}
        />
      );
    } else {
      return (
        <Input
          id={field.name}
          name={field.name}
          aria-label={field.label}
          required={isRequired(field, record)}
          placeholder={field.type === "text" ? field.placeholder : undefined}
          minLength={field.type === "text" ? field.minLength : undefined}
          maxLength={field.type === "text" ? field.maxLength : undefined}
          type={field.inputType ?? "text"}
          onChange={(e) => field.onChange(e.target.value)}
          value={field.value()}
          disabled={isDisabled(field, record)}
          {...props}
        />
      );
    }
  },

  number: ({ field, record, ...props }: BoundValueComponentProps<NumberField>) => (
    <Input
      id={field.name}
      name={field.name}
      aria-label={field.label}
      required={isRequired(field, record)}
      placeholder={field.type === "number" ? field.placeholder : undefined}
      min={field.type === "number" ? field.min : undefined}
      max={field.type === "number" ? field.max : undefined}
      step={field.type === "number" ? field.step : undefined}
      type="number"
      onChange={(e) => field.onChange(Number(e.target.value))}
      value={field.value()}
      prefix={field.prefix}
      disabled={isDisabled(field, record)}
      {...props}
    />
  ),

  dollars: ({ field, record, ...props }: BoundValueComponentProps<DollarsField>) => {
    if (field.type !== "dollars") return null;
    const cents = record[field.name as keyof typeof record] as number | undefined | null;
    const displayPounds = centsToDollars(cents);
    const valueStr = displayPounds != null ? String(displayPounds) : "";
    return (
      <Input
        id={field.name}
        name={field.name}
        aria-label={field.label}
        type="number"
        min={field.min ?? 0}
        max={field.max}
        step={field.step ?? 0.01}
        placeholder={field.placeholder}
        required={isRequired(field, record)}
        disabled={isDisabled(field, record)}
        value={valueStr}
        onChange={(e) => {
          const cents = dollarsToCents(e.target.value);
          (record as Record<string, unknown>)[field.name] = cents;
          field.onChange?.(cents as number);
        }}
        {...props}
      />
    );
  },

  select: ({
    field,
    record,
    variant: propVariant,
    ...props
  }: BoundValueComponentProps<SelectField<any>> & {
    variant?: "select" | "radio-horizontal" | "radio-vertical";
  }) => {
    if (field.type !== "select") return null;

    // Use prop variant if provided, otherwise determine based on options
    const variant =
      propVariant ??
      field.forceVariant ??
      (() => {
        const hasDescription = field.options?.some((opt) => opt.description);
        const hasIcons = field.options?.some((opt) => opt.icon);

        if (hasDescription) {
          return "radio-vertical";
        }
        if (hasIcons) {
          return "radio-horizontal";
        }
        return "select";
      })();

    if (variant === "select") {
      return (
        <Select
          id={field.name}
          name={field.name}
          aria-label={field.label}
          onChange={(e) => field.onChange(e.target.value)}
          value={field.value()}
          required={isRequired(field, record)}
          disabled={isDisabled(field, record)}
          {...props}
        >
          {!field.hidePlaceholderOption && <option value="">Select...</option>}
          {field.options?.map((option: FormFieldOption<any>) => (
            <option key={option.value} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </Select>
      );
    }

    return (
      <RadioButtonGroup
        name={field.name}
        onChange={(e) => field.onChange(String(e.target.value))}
        value={field.value()}
        options={field.options ?? []}
        variant={variant === "radio-horizontal" ? "horizontal" : "vertical"}
        disabled={isDisabled(field, record)}
      />
    );
  },

  checkbox: ({
    field,
    variant,
    record,
  }: BoundValueComponentProps<CheckboxField> & {
    variant?: "checkbox" | "toggle" | "button";
  }) => {
    if (field.type !== "checkbox") return null;

    if (variant === "button" && field.buttonVariant) {
      const isChecked = field.value() ?? false;
      const {
        checkedLabel,
        uncheckedLabel,
        checkedKind = "danger",
        uncheckedKind = "accent",
      } = field.buttonVariant;

      return (
        <div>
          <Button
            type="button"
            kind={isChecked ? checkedKind : uncheckedKind}
            onClick={() => field.onChange(!isChecked)}
            disabled={isDisabled(field, record)}
          >
            {isChecked ? checkedLabel : uncheckedLabel}
          </Button>
        </div>
      );
    }

    if (variant === "toggle") {
      return (
        <Toggle
          name={field.name}
          checked={field.value() ?? false}
          onChange={(e) => field.onChange(e.target.checked)}
          id={field.name}
          required={isRequired(field, record)}
          disabled={isDisabled(field, record)}
        />
      );
    }

    return (
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={field.value() ?? false}
          onChange={(e) => field.onChange(e.target.checked)}
          className="w-4 h-4 border-gray-300 rounded"
          required={isRequired(field, record)}
          disabled={isDisabled(field, record)}
        />
        {field.text && <span>{field.text}</span>}
      </label>
    );
  },

  datetime: ({ field, record }: BoundValueComponentProps<DatetimeField>) => {
    if (field.type !== "datetime") return null;

    const format = field.includeTime ? "YYYY-MM-DDTHH:mm" : "YYYY-MM-DD";
    const value = dayjs(field.value()).format(format);
    const type = field.includeTime ? "datetime-local" : "date";

    return (
      <Input
        id={field.name}
        name={field.name}
        aria-label={field.label}
        type={type}
        onChange={(e) => field.onChange(dayjs(e.target.value).toDate())}
        value={value}
        disabled={isDisabled(field, record)}
      />
    );
  },

  time: ({ field, record, ...props }: BoundValueComponentProps<TimeField>) => {
    if (field.type !== "time") return null;

    return (
      <Input
        id={field.name}
        name={field.name}
        aria-label={field.label}
        type="time"
        required={isRequired(field, record)}
        onChange={(e) => field.onChange(e.target.value)}
        value={field.value()}
        disabled={isDisabled(field, record)}
        {...props}
      />
    );
  },

  custom: ({ field, record }: BoundValueComponentProps<CustomField>) => {
    if (field.type !== "custom") return null;

    const CustomComponent = field.component;
    return CustomComponent ? (
      <CustomComponent
        onChange={(value: any) => field.onChange(value)}
        value={field.value()}
        field={field}
        record={record}
      />
    ) : null;
  },

  html: ({ field, record }: BoundValueComponentProps<HtmlField>) => {
    const editorClassName = field.className ?? "max-w-[570px]";
    return (
      <Editor
        className={editorClassName}
        onChange={(value) => {
          field.onChange(value.target.value);
        }}
        value={field.value() ?? ""}
        insertableVariables={field.insertableVariables}
        sanitizeHtmlFn={field.sanitizeHtml}
        disabled={isDisabled(field, record)}
      />
    );
  },
  upload: ({ field, className }: BoundValueComponentProps<UploadField>) => {
    if (field.variant === "image") {
      return (
        <ImageUpload
          value={field.value()}
          onChange={(value) => field.onChange(value)}
          className={className}
        />
      );
    } else if (field.variant === "pdf") {
      return (
        <PdfUpload value={field.value()} onChange={(value) => field.onChange(value)} className={className} />
      );
    }
  },

  segmented: ({ field, record, ...props }: BoundValueComponentProps<SegmentedField>) => {
    if (field.type !== "segmented") return null;

    return (
      <SegmentedInput
        format={field.format}
        value={field.value()}
        onChange={(value) => field.onChange(value)}
        showValid={field.showValid}
        prefix={field.prefix}
        suffix={field.suffix}
        disabled={isDisabled(field, record)}
        {...props}
      />
    );
  },
};

// Stable ApplicationView wrappers — creating these inside render remounts inputs on every
// keystroke and re-triggers autoFocus on the first field.
const BoundValueComponents = {
  text: ApplicationView(ValueComponents.text),
  number: ApplicationView(ValueComponents.number),
  dollars: ApplicationView(ValueComponents.dollars),
  select: ApplicationView(ValueComponents.select),
  checkbox: ApplicationView(ValueComponents.checkbox),
  datetime: ApplicationView(ValueComponents.datetime),
  time: ApplicationView(ValueComponents.time),
  custom: ApplicationView(ValueComponents.custom),
  html: ApplicationView(ValueComponents.html),
  upload: ApplicationView(ValueComponents.upload),
  segmented: ApplicationView(ValueComponents.segmented),
} as unknown as Record<string, React.ComponentType<ValueComponentProps>>;

function ControlledValueComponent<_F extends FormFieldConfig>({
  field,
  record,
  ...props
}: ValueComponentProps) {
  // Directory signup and other non-form wizards reuse ValueComponent without a FormController.
  const controller = FormController.useOptional();
  const onChange = useCallback(
    (value: any) => {
      if (field.onChange) field.onChange(value);
      else record[field.name] = value;
    },
    [field, record],
  );

  const formField = useMemo(
    () =>
      ({
        ...field,
        onChange,
        value() {
          if (field.value) return field.value();
          return record[field.name];
        },
        validate:
          field.validate ||
          ((value: any) => {
            if (field.required && !value) return "This field is required";
            return undefined;
          }),
      }) as FormField<any>,
    [field, onChange, record],
  );

  const Component = BoundValueComponents[field.type] ?? BoundValueComponents.custom;
  return (
    <Component field={formField} record={record} error={controller?.state.errors?.[field.name]} {...props} />
  );
}

export const ValueComponent = ApplicationView(ControlledValueComponent);
